/**
 * Service for managing direct authentication token in development/debug mode
 * Allows bypassing MSAL authentication when running on localhost
 */

export class DirectTokenService {
    private static readonly STORAGE_KEY = 'direct_auth_token';
    private static readonly STORAGE_KEY_USERNAME = 'direct_auth_username';

    /**
     * Check if we're in localhost or debug mode
     * Only checks for actual localhost hostnames to prevent showing in production
     */
    static isLocalhostOrDebug(): boolean {
        const hostname = window.location.hostname.toLowerCase();
        return (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname === '[::1]'
        );
    }

    /**
     * Store a direct auth token
     */
    static setDirectToken(token: string, username?: string): void {
        if (!this.isLocalhostOrDebug()) {
            console.warn('⚠️ Direct token mode is only available in localhost/debug mode');
            return;
        }
        sessionStorage.setItem(this.STORAGE_KEY, token);
        if (username) {
            sessionStorage.setItem(this.STORAGE_KEY_USERNAME, username);
        }
    }

    /**
     * Get the stored direct auth token
     */
    static getDirectToken(): string | null {
        if (!this.isLocalhostOrDebug()) {
            return null;
        }
        return sessionStorage.getItem(this.STORAGE_KEY);
    }

    /**
     * Get the stored username (if any)
     */
    static getDirectTokenUsername(): string | null {
        if (!this.isLocalhostOrDebug()) {
            return null;
        }
        return sessionStorage.getItem(this.STORAGE_KEY_USERNAME);
    }

    /**
     * Clear the direct auth token
     */
    static clearDirectToken(): void {
        sessionStorage.removeItem(this.STORAGE_KEY);
        sessionStorage.removeItem(this.STORAGE_KEY_USERNAME);
    }

    /**
     * Check if direct token is available
     */
    static hasDirectToken(): boolean {
        return this.isLocalhostOrDebug() && this.getDirectToken() !== null;
    }
}

